import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { authApi } from '@/features/auth/api';
import { brandsApi } from '@/features/brands/api';
import { useAuth } from '@/lib/auth/useAuth';
import { useAuthStore } from '@/lib/auth/authStore';
import {
  useBrandContextStore,
  useSelectedBrandId,
} from '@/lib/branding/brandContextStore';
import { useBrandPreferenceSync } from '@/lib/branding/useBrandPreferenceSync';
import { queryKeys } from '@/lib/api/queryKeys';

/**
 * Después de `ProtectedRoute`, esta puerta impone el árbol de alta:
 *   - rol staff/viewer   → /pending-access
 *   - sin companyId      → /onboarding/brand-profile
 *   - usuario no-full sin marca seleccionada → elige la primera accesible
 *   - usuario no-full en `/`                 → va a `/brand/:selectedBrandId`
 *   - si no                                  → pinta
 *
 * ## Ya no se exige un kit de marca para entrar
 *
 * Había un paso más —subir logo y colores en `/onboarding/brandkit`— y se
 * quitó. Era fricción puesta delante del momento en que el usuario todavía no
 * sabe si el producto le sirve, y no desbloqueaba nada: `DEFAULT_BRAND_KIT`
 * cubre la ausencia y el runtime público se pinta igual.
 *
 * **Lo que sí se conserva es `/onboarding/brand-profile`**, que no es estética:
 * pide el nombre de la empresa, y sin él el espacio de trabajo no existe.
 *
 * Personalizar la marca sigue disponible en Ajustes › Marca, y aparece como
 * sugerencia en la lista de primeros pasos. Es un empujón, no un muro.
 *
 * Se sigue consultando `GET /auth/onboarding-status` para saber si ya hay kit
 * —lo usa esa sugerencia— pero **ya no se espera a la respuesta para pintar**:
 * cuando bloqueaba, cada inicio de sesión pasaba por un fotograma en blanco.
 */
export function OnboardingGate() {
  const { user, role } = useAuth();
  const patchUser = useAuthStore((s) => s.patchUser);
  const app = useAuthStore((s) => s.app);
  const location = useLocation();
  const selectedBrandId = useSelectedBrandId();
  const setSelectedBrandId = useBrandContextStore((s) => s.setSelectedBrandId);

  const statusQ = useQuery({
    queryKey: queryKeys.auth.onboarding,
    queryFn: () => authApi.onboardingStatus(),
    enabled: !!user && user.hasBrandKit === undefined,
    staleTime: 60_000,
    retry: false,
  });

  // Brands accessible to the logged-in user. Used to auto-select the first
  // one for non-full users so they always have a working brand context.
  const isFullRole = role === 'admin' || role === 'master';
  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
    enabled: !!user?.companyId,
    staleTime: 60_000,
  });
  const brands = brandsQ.data?.items ?? [];
  const brandIds = useMemo(() => brands.map((b) => b.id), [brands]);
  // 095 — la última marca del usuario, desde el servidor y hacia el servidor.
  useBrandPreferenceSync(brandIds);
  const hydratedFor = useBrandContextStore((s) => s.hydratedFor);

  useEffect(() => {
    if (statusQ.data && user?.hasBrandKit === undefined) {
      patchUser({ hasBrandKit: statusQ.data.hasBrandKit });
    }
  }, [statusQ.data, user?.hasBrandKit, patchUser]);

  // Repair stale or missing brand selection. If the persisted id no longer
  // matches any accessible brand (revoked access, deleted brand), drop it
  // and pick the first available so non-full users never see a blank brand.
  useEffect(() => {
    if (brands.length === 0) return;
    // Después de hidratar desde el servidor, no antes: si no, la corrección
    // ganaría a la marca guardada y la reescribiría en cada dispositivo.
    if (user?.id && hydratedFor !== user.id) return;
    const stillValid =
      selectedBrandId != null && brands.some((b) => b.id === selectedBrandId);
    if (!stillValid) {
      setSelectedBrandId(brands[0].id, 'repair');
    }
  }, [brands, selectedBrandId, setSelectedBrandId, hydratedFor, user?.id]);

  // Caso 11 — operator sessions. The token exchange
  // (`POST /xk-station/auth/exchange`) issues a JWT with `app: 'operator'`,
  // and OperatorLoginPage stamps a synthetic `role: 'staff'` on the session
  // (the real restriction is the server-side `app` claim, not a FE role).
  // That synthetic role would otherwise trip the staff/viewer branch below
  // and bounce the operator to /pending-access. Operator sessions are
  // legitimate — let them straight through the whole onboarding tree so the
  // station view and its redeem/QR shortcuts render normally.
  if (app === 'operator') {
    return <Outlet />;
  }

  if (role === 'staff' || role === 'viewer') {
    if (location.pathname !== '/pending-access') {
      return <Navigate to="/pending-access" replace />;
    }
    return <Outlet />;
  }

  if (!user?.companyId && !location.pathname.startsWith('/onboarding/')) {
    return <Navigate to="/onboarding/brand-profile" replace />;
  }

  // Con el espacio ya creado, el alta está terminada: quien vuelva a una URL
  // de /onboarding —un enlace guardado, el botón atrás— sale al panel.
  if (user?.companyId && location.pathname.startsWith('/onboarding/')) {
    return <Navigate to="/" replace />;
  }

  // Non-full users: home is the selected brand profile, not the agency
  // dashboard. Wait for the brands query so we don't redirect to /brand/0.
  if (
    user?.companyId &&
    !isFullRole &&
    location.pathname === '/' &&
    !brandsQ.isLoading
  ) {
    const targetId =
      selectedBrandId && brands.some((b) => b.id === selectedBrandId)
        ? selectedBrandId
        : brands[0]?.id;
    if (targetId) {
      return <Navigate to={`/brand/${targetId}`} replace />;
    }
  }

  return <Outlet />;
}
